// src/services/loggingService.js
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';

// Log levels
export const LOG_LEVELS = {
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
  CRITICAL: 'critical',
  DEBUG: 'debug'
};

// Keep track of logged events to prevent duplicates
const loggedEvents = new Set();

/**
 * Generate a unique log ID to prevent duplicates
 * @param {string} category - Category of event
 * @param {string} action - Specific action
 * @param {string|null} userId - User ID if authenticated
 * @returns {string} - A unique ID for this log event
 */
const generateLogId = (category, action, userId) => {
  const timestamp = Date.now();
  return `${category}_${action}_${userId || 'anonymous'}_${timestamp}`;
};

/**
 * Log an event to Firestore
 * @param {string} category - Category of event (e.g., 'auth', 'search')
 * @param {string} action - Specific action (e.g., 'login_success')
 * @param {string} level - Severity level
 * @param {Object} details - Additional event details
 * @param {string|null} userId - User ID if authenticated
 * @returns {string|null} Log document ID or null if logging failed
 */
export const logEvent = async (category, action, level = LOG_LEVELS.INFO, details = {}, userId = null) => {
  try {
    // Check if user is authenticated before attempting to write to Firestore
    const currentUser = auth.currentUser;
    
    // Generate a unique event ID for deduplication
    const eventId = generateLogId(category, action, userId || (currentUser ? currentUser.uid : null));
    
    // Skip if this exact event was already logged in this session
    if (loggedEvents.has(eventId)) {
      console.log(`Skipping duplicate log event: ${category}:${action}`);
      return null;
    }
    
    // Add to logged events set
    loggedEvents.add(eventId);
    
    // Debug authentication state
    console.log('Authentication state:', {
      currentUser: currentUser ? {
        uid: currentUser.uid,
        email: currentUser.email,
        isAnonymous: currentUser.isAnonymous,
        emailVerified: currentUser.emailVerified
      } : null,
      providedUserId: userId
    });
    
    // Create a reference to the logs collection
    const logsRef = collection(db, 'activity-logs');
    
    // Create the log entry
    const logEntry = {
      timestamp: serverTimestamp(),
      category,
      action,
      level,
      details,
      userId: userId || (currentUser ? currentUser.uid : null),
      userAgent: navigator.userAgent,
      clientTimestamp: new Date().toISOString(), // Add client timestamp for reference
    };
    
    // Development logging
    if (process.env.NODE_ENV !== 'production') {
      console.log(`[${level}] ${category}:${action}`, logEntry);
    }
    
    // For testing purposes, you can set this environment variable to bypass authentication check
    const bypassAuthForTesting = process.env.REACT_APP_BYPASS_AUTH_FOR_TESTING === 'true';
    
    // Only attempt to write to Firestore if user is authenticated or if testing bypass is enabled
    if (currentUser || bypassAuthForTesting) {
      try {
        console.log('Attempting to write log to Firestore...');
        // Add to Firestore with a random document ID
        const docRef = await addDoc(logsRef, logEntry);
        console.log('Successfully wrote log to Firestore with ID:', docRef.id);
        return docRef.id;
      } catch (error) {
        if (error.code === 'permission-denied') {
          console.warn('Permission denied when logging event. Make sure you are authenticated or add a test user in Firestore rules.');
          console.error('Full error:', error);
          
          // For development, provide helpful guidance
          if (process.env.NODE_ENV !== 'production') {
            console.info('To test logging without authentication, you can:');
            console.info('1. Sign in to the application first');
            console.info('2. Add REACT_APP_BYPASS_AUTH_FOR_TESTING=true to your .env file and update Firestore rules');
            console.info('3. Create a test user in Firebase Authentication');
          }
        } else {
          console.error('Error adding log to Firestore:', error);
        }
        return null;
      }
    } else {
      // Just log to console if not authenticated
      console.log('Not logging to Firestore: User not authenticated');
      return null;
    }
  } catch (error) {
    console.error('Error in logEvent function:', error);
    return null;
  }
};

// Clear logged events cache after 5 minutes to prevent memory leaks
setInterval(() => {
  loggedEvents.clear();
}, 5 * 60 * 1000);

// Specialized logging functions
export const logAuthEvent = (action, details = {}, level = LOG_LEVELS.INFO, userId = null) => {
  return logEvent('auth', action, level, details, userId);
};

export const logSearchEvent = (action, details = {}, level = LOG_LEVELS.INFO, userId = null) => {
  return logEvent('search', action, level, details, userId);
};

export const logAdminEvent = (action, details = {}, level = LOG_LEVELS.INFO, userId = null) => {
  return logEvent('admin', action, level, details, userId);
};

export const logSecurityEvent = (action, details = {}, level = LOG_LEVELS.WARNING, userId = null) => {
  return logEvent('security', action, level, details, userId);
};

export const logErrorEvent = (error, context = {}, userId = null) => {
  return logEvent(
    context.category || 'error',
    context.action || 'unspecified_error',
    LOG_LEVELS.ERROR,
    {
      message: error.message,
      code: error.code,
      stack: process.env.NODE_ENV !== 'production' ? error.stack : undefined,
      ...context
    },
    userId
  );
};
